import React, { useId } from 'react';

// Web port of iOS AvatarFrameView's FrameStyle engine. Frames are drawn as crisp
// SVG vectors (normalized to a 100-unit box → scales to any avatar size) so they
// stay sharp from 36px ranking rows to the 72px+ profile hero, adapt to light/
// dark via the --frame-* CSS vars (theme.css), and animate on the hero only.
//
// Geometry mirrors AvatarFrameView.swift exactly (every `size * k` becomes
// `100 * k`); strokeBorder (stroke drawn INSIDE the circle) is reproduced by
// insetting the SVG circle radius by half the stroke width. Ornaments (gems,
// crown, sparkles, charms, ticks) draw outside the 0–100 box and rely on the
// svg `overflow: visible`, so the parent must not clip (Avatar reserves
// headroom for top-overhanging styles).
//
// Context tiers match iOS: `animated` (profile/member-sheet hero) plays the
// pulse/orbit/sparkle/aurora motion; everywhere else renders the at-rest geometry.

// ── id → (style, colorToken, category) — port of AvatarFrameView.descriptor ──
export function frameDescriptor(id) {
  if (!id || typeof id !== 'string') return { style: 'hairline', color: 'gold', category: null };
  if (id.startsWith('frame-awarded-monthly-winner')) return { style: 'crown', color: 'gold', category: null };
  switch (id) {
    case 'frame-static-gold-thin': return { style: 'hairline', color: 'gold', category: null };
    case 'frame-static-gold-thick': return { style: 'bold', color: 'gold', category: null };
    case 'frame-anim-pulse': return { style: 'pulse', color: 'gold', category: null };
    case 'frame-anim-rotation': return { style: 'orbit', color: 'gold', category: null };
    case 'frame-anim-sparkle': return { style: 'sparkle', color: 'gold', category: null };
    case 'frame-anim-aurora': return { style: 'aurora', color: 'gold', category: null };
    case 'frame-awarded-founder': return { style: 'laurel', color: 'gold', category: null };
    case 'frame-awarded-streak-30':
    case 'frame-awarded-streak-100': return { style: 'flame', color: 'amber', category: null };
    default: break;
  }
  const cats = ['markets', 'politics', 'sports', 'culture', 'technology', 'world'];
  for (const c of cats) if (id === `frame-static-${c}`) return { style: 'categoryEmblem', color: c, category: c };
  if (id.startsWith('frame-cat-')) {
    const c = id.slice('frame-cat-'.length);
    return { style: 'categoryEmblem', color: c, category: c };
  }
  if (id.startsWith('frame-')) {
    const parts = id.slice('frame-'.length).split('-');
    return { style: styleForToken(parts[0]), color: parts[1] || 'gold', category: null };
  }
  return { style: 'doubleRing', color: 'gold', category: null }; // distinct fallback (catalog skew)
}

function styleForToken(token) {
  switch (token) {
    case 'hairline': return 'hairline';
    case 'bold': return 'bold';
    case 'double': return 'doubleRing';
    case 'dashed': return 'dashed';
    case 'gem': return 'gemStuds';
    case 'metal': return 'brushedMetal';
    case 'pulse': return 'pulse';
    case 'orbit': return 'orbit';
    case 'sparkle': return 'sparkle';
    case 'aurora': return 'aurora';
    default: return 'hairline';
  }
}

/** Known frame color tokens → the --frame-* CSS var (reactive to light/dark). */
function colorVar(token) {
  const known = new Set([
    'gold', 'crimson', 'sapphire', 'amber', 'amethyst', 'emerald',
    'markets', 'politics', 'sports', 'culture', 'technology', 'world',
  ]);
  return known.has(token) ? `var(--frame-${token})` : 'var(--frame-gold)';
}

/** Extra top space (in px) a style's ornament needs above the avatar so layout
 *  above it doesn't overlap. Mirrors AvatarFrameView.topOverhang. */
export function frameTopOverhang(frameId, size, animated = false) {
  const { style } = frameDescriptor(frameId);
  if (style === 'crown') return size * (animated ? 0.30 : 0.16);
  if (style === 'laurel' || style === 'gemStuds' || style === 'orbit') return size * 0.15;
  return 0;
}

// ── ring helper (strokeBorder semantics → inset radius by half the width) ────
function ring(w, stroke, extra = {}) {
  return <circle cx="50" cy="50" r={50 - w / 2} fill="none" stroke={stroke} strokeWidth={w} {...extra} />;
}

export default function AvatarFrame({ frameId, size = 72, animated = false }) {
  const uid = useId().replace(/:/g, '');
  const { style, color, category } = frameDescriptor(frameId);
  const c = colorVar(color);
  const glow = Math.max(2, size * 0.08);

  const wrapStyle = {
    position: 'absolute',
    inset: 0,
    width: size,
    height: size,
    pointerEvents: 'none',
    filter: `drop-shadow(0 0 ${glow}px ${c})`,
  };

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ ...wrapStyle, overflow: 'visible' }}
      aria-hidden="true"
    >
      {renderStyle({ style, c, category, animated, uid, size })}
    </svg>
  );
}

function renderStyle({ style, c, category, animated, uid, size }) {
  switch (style) {
    case 'hairline': return ring(5, c);
    case 'bold': return ring(12, c);
    case 'doubleRing':
      return (<>{ring(6, c)}<circle cx="50" cy="50" r={40 - 1.25} fill="none" stroke={c} strokeWidth="2.5" /></>);
    case 'dashed':
      return ring(7, c, { strokeDasharray: '13 8', strokeLinecap: 'round' });
    case 'gemStuds':
      return (
        <>
          {ring(6, c)}
          {Array.from({ length: 8 }, (_, i) => (
            <g key={i} transform={`rotate(${(i / 8) * 360} 50 50)`}>
              <circle cx="50" cy="2" r="4.5" fill={c} />
            </g>
          ))}
        </>
      );
    case 'brushedMetal':
      return (
        <>
          <defs>
            <linearGradient id={`metal-${uid}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.5" />
              <stop offset="35%" stopColor="#fff" stopOpacity="0" />
              <stop offset="55%" stopColor="#000" stopOpacity="0.28" />
              <stop offset="75%" stopColor="#fff" stopOpacity="0" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          {ring(10, c)}
          {ring(10, `url(#metal-${uid})`)}
        </>
      );
    case 'categoryEmblem':
      return (
        <>
          {ring(7, c)}
          <circle cx="50" cy="96" r="15" fill={c} />
          <Glyph name={category} cx={50} cy={96} size={17} color="#fff" />
        </>
      );
    case 'pulse':
      return (
        <>
          {animated && (
            <circle
              className="ps-frame__pulse"
              cx="50" cy="50" r={50 - 4.5}
              fill="none" stroke={c} strokeWidth="9"
              style={{ filter: 'blur(3px)' }}
            />
          )}
          {ring(9, c)}
        </>
      );
    case 'orbit':
      return (
        <>
          {ring(9, c)}
          <g className={animated ? 'ps-frame__orbit' : undefined}>
            {/* sweeping highlight arc (brighter segment of the ring) */}
            <circle
              cx="50" cy="50" r={50 - 4.5}
              fill="none" stroke="#fff" strokeOpacity="0.4" strokeWidth="9"
              strokeDasharray="70 230" strokeLinecap="round"
            />
            <circle cx="50" cy="0" r="6" fill={c} />
          </g>
        </>
      );
    case 'sparkle':
      return (
        <>
          {ring(7, c)}
          {[
            { x: 84, y: 16 },
            { x: 10, y: 56 },
            { x: 58, y: 92 },
          ].map((p, i) => (
            <g
              key={i}
              className={animated ? 'ps-frame__sparkle' : undefined}
              style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            >
              <Glyph name="sparkle" cx={p.x} cy={p.y} size={20} color={c} />
            </g>
          ))}
        </>
      );
    case 'aurora':
      return (
        <>
          <defs>
            <linearGradient id={`aurora-${uid}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#C9973A" />
              <stop offset="40%" stopColor="#E8DCC0" />
              <stop offset="70%" stopColor="#5DCAA5" />
              <stop offset="100%" stopColor="#C9973A" />
            </linearGradient>
          </defs>
          {ring(9, c)}
          <g className={animated ? 'ps-frame__aurora' : undefined}>
            {ring(9, `url(#aurora-${uid})`)}
          </g>
        </>
      );
    case 'laurel':
      return (
        <>
          {ring(8, c)}
          <circle cx="50" cy="50" r={41 - 1} fill="none" stroke={c} strokeWidth="2" />
          {Array.from({ length: 16 }, (_, i) => (
            <g key={i} transform={`rotate(${(i / 16) * 360} 50 50)`}>
              <rect x="49.1" y="-2.5" width="1.8" height="5" rx="0.9" fill={c} />
            </g>
          ))}
        </>
      );
    case 'crown':
      return (
        <>
          {ring(8, c)}
          <Glyph name="crown" cx={50} cy={animated ? -2 : 4} size={26} color={c} />
        </>
      );
    case 'flame':
      return (
        <>
          {ring(7, 'var(--frame-fire)')}
          <circle cx="50" cy="96" r="15" fill="var(--frame-fire)" />
          <Glyph name="flame" cx={50} cy={96} size={16} color="#fff" />
        </>
      );
    default:
      return ring(5, c);
  }
}

// ── Glyphs — minimal SF-Symbol-equivalents drawn in a 0–24 box, scaled to
//    `size` units and centered at (cx, cy). White on colored charm discs;
//    frame-colored for crown/sparkle. ─────────────────────────────────────────
function Glyph({ name, cx, cy, size, color }) {
  const s = size / 24;
  const t = `translate(${cx} ${cy}) scale(${s}) translate(-12 -12)`;
  const stroke = { stroke: color, strokeWidth: 2.4, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
  let body;
  switch (name) {
    case 'crown':
      body = (
        <>
          <path d="M3 18 L5 9 L8.5 13 L12 6 L15.5 13 L19 9 L21 18 Z" fill={color} />
          <rect x="3.5" y="18" width="17" height="3" rx="1" fill={color} />
        </>
      );
      break;
    case 'flame':
      body = (
        <path
          d="M12 2 C12 2 6 8 6 14 A6 6 0 0 0 18 14 C18 9 14 8 13 4 C12.6 6 11 7 11 9 C9.5 7.5 11 4 12 2 Z"
          fill={color}
        />
      );
      break;
    case 'sparkle':
      body = <path d="M12 1 L13.8 10.2 L23 12 L13.8 13.8 L12 23 L10.2 13.8 L1 12 L10.2 10.2 Z" fill={color} />;
      break;
    case 'markets':
      body = (
        <>
          <path d="M3 17 L9 11 L13 14 L21 6" {...stroke} />
          <path d="M16 6 H21 V11" {...stroke} />
        </>
      );
      break;
    case 'politics':
      body = (
        <>
          <path d="M2 8 L12 3 L22 8" {...stroke} />
          <path d="M4 8 V18 M9 8 V18 M15 8 V18 M20 8 V18" {...stroke} />
          <path d="M3 20 H21" {...stroke} />
        </>
      );
      break;
    case 'sports':
      body = (
        <>
          <rect x="3" y="6" width="18" height="12" rx="2" {...stroke} />
          <path d="M12 6 V18" {...stroke} />
          <circle cx="12" cy="12" r="2.6" {...stroke} />
        </>
      );
      break;
    case 'culture':
      body = (
        <>
          <rect x="4" y="4" width="16" height="16" rx="2" {...stroke} />
          <path d="M8 4 V20 M16 4 V20" {...stroke} />
        </>
      );
      break;
    case 'technology':
      body = (
        <>
          <rect x="5" y="5" width="14" height="9" rx="1" {...stroke} />
          <path d="M3 19 L5 14 H19 L21 19 Z" fill={color} />
        </>
      );
      break;
    case 'world':
      body = (
        <>
          <circle cx="12" cy="12" r="9" {...stroke} />
          <ellipse cx="12" cy="12" rx="4" ry="9" {...stroke} />
          <path d="M3 12 H21" {...stroke} />
        </>
      );
      break;
    default:
      body = <circle cx="12" cy="12" r="3" fill={color} />;
  }
  return <g transform={t}>{body}</g>;
}
